import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

interface SizeEntry {
  date: string;
  label?: string;
  shirt?: string;
  pants_waist?: string;
  pants_length?: string;
  shoe?: string;
  dress?: string;
  jacket?: string;
  hat?: string;
  gloves?: string;
  ring?: string;
  underwear?: string;
  socks?: string;
}

interface ClothingSizesData {
  entries: SizeEntry[];
  notes?: string;
}

interface ClothingSizesDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ClothingSizesData) => Promise<void>;
  personName: string;
  data?: ClothingSizesData | null;
}

const SIZE_FIELDS: { key: keyof Omit<SizeEntry, 'date' | 'label'>; label: string; placeholder: string }[] = [
  { key: 'shirt', label: 'Shirt / Top', placeholder: 'e.g., M, L, XL' },
  { key: 'pants_waist', label: 'Pants Waist', placeholder: 'e.g., 32' },
  { key: 'pants_length', label: 'Pants Length', placeholder: 'e.g., 30' },
  { key: 'shoe', label: 'Shoe', placeholder: 'e.g., 10, 42 EU' },
  { key: 'dress', label: 'Dress', placeholder: 'e.g., 8, M' },
  { key: 'jacket', label: 'Jacket / Coat', placeholder: 'e.g., L, 42R' },
  { key: 'hat', label: 'Hat', placeholder: 'e.g., 7 1/4, L' },
  { key: 'gloves', label: 'Gloves', placeholder: 'e.g., M, 8' },
  { key: 'ring', label: 'Ring', placeholder: 'e.g., 7, 8.5' },
  { key: 'underwear', label: 'Underwear', placeholder: 'e.g., M, L' },
  { key: 'socks', label: 'Socks', placeholder: 'e.g., 10-13, M' },
];

const todayString = () => new Date().toISOString().split('T')[0];

const formatDate = (dateStr: string) => {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
};

const entrySummary = (entry: SizeEntry) =>
  SIZE_FIELDS.filter((f) => entry[f.key])
    .map((f) => `${f.label}: ${entry[f.key]}`)
    .join(', ') || 'No sizes recorded';

export const ClothingSizesDialog: React.FC<ClothingSizesDialogProps> = ({
  open,
  onClose,
  onSave,
  personName,
  data,
}) => {
  const [entries, setEntries] = useState<SizeEntry[]>([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editEntry, setEditEntry] = useState<SizeEntry>({ date: todayString() });
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      if (data) {
        setEntries([...(data.entries || [])]);
        setNotes(data.notes || '');
      } else {
        setEntries([]);
        setNotes('');
      }
      setError(null);
      setEditingIndex(null);
      setExpandedIndex(null);
    }
  }, [open, data]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const cleanData: ClothingSizesData = {
        entries: entries.sort((a, b) => b.date.localeCompare(a.date)),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      };
      await onSave(cleanData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save clothing sizes');
    } finally {
      setSaving(false);
    }
  };

  const startNewEntry = () => {
    setEditEntry({ date: todayString() });
    setEditingIndex(-1);
  };

  const startEditEntry = (index: number) => {
    setEditEntry({ ...entries[index] });
    setEditingIndex(index);
  };

  const saveEntry = () => {
    const clean: SizeEntry = { date: editEntry.date };
    if (editEntry.label?.trim()) clean.label = editEntry.label.trim();
    for (const field of SIZE_FIELDS) {
      const val = editEntry[field.key];
      if (val && val.trim()) (clean as any)[field.key] = val.trim();
    }
    const updated = [...entries];
    if (editingIndex === -1) {
      updated.unshift(clean);
    } else if (editingIndex !== null) {
      updated[editingIndex] = clean;
    }
    setEntries(updated.sort((a, b) => b.date.localeCompare(a.date)));
    setEditingIndex(null);
  };

  const deleteEntry = (index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEntryFieldChange = (key: string, value: string) => {
    setEditEntry((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Clothing Sizes — {personName}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}

        {/* Entry Editor */}
        {editingIndex !== null && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {editingIndex === -1 ? 'New Size Entry' : 'Edit Entry'}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Date"
                  type="date"
                  value={editEntry.date}
                  onChange={(e) => handleEntryFieldChange('date', e.target.value)}
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Label (optional)"
                  value={editEntry.label || ''}
                  onChange={(e) => handleEntryFieldChange('label', e.target.value)}
                  fullWidth
                  placeholder="e.g., Back to school"
                />
              </Grid>
              {SIZE_FIELDS.map((field) => (
                <Grid size={{ xs: 6 }} key={field.key}>
                  <TextField
                    label={field.label}
                    value={editEntry[field.key] || ''}
                    onChange={(e) => handleEntryFieldChange(field.key, e.target.value)}
                    fullWidth
                    placeholder={field.placeholder}
                    size="small"
                  />
                </Grid>
              ))}
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
              <Button onClick={() => setEditingIndex(null)}>Cancel</Button>
              <Button variant="contained" onClick={saveEntry}>
                {editingIndex === -1 ? 'Add' : 'Update'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Entry List */}
        {editingIndex === null && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">
                Size History ({entries.length} {entries.length === 1 ? 'entry' : 'entries'})
              </Typography>
              <Button startIcon={<AddIcon />} size="small" onClick={startNewEntry}>
                Add Entry
              </Button>
            </Box>

            {entries.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No size entries yet. Add one to start tracking.
              </Typography>
            )}

            <List disablePadding>
              {entries.map((entry, index) => (
                <React.Fragment key={`${entry.date}-${index}`}>
                  {index > 0 && <Divider />}
                  <ListItem
                    sx={{ cursor: 'pointer', px: 1 }}
                    onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {formatDate(entry.date)}
                          </Typography>
                          {entry.label && (
                            <Chip label={entry.label} size="small" color="primary" variant="outlined" />
                          )}
                          {index === 0 && <Chip label="Current" size="small" color="success" />}
                        </Box>
                      }
                      secondary={expandedIndex !== index ? entrySummary(entry) : undefined}
                      secondaryTypographyProps={{ noWrap: true }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); startEditEntry(index); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteEntry(index); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      {expandedIndex === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Collapse in={expandedIndex === index}>
                    <Box sx={{ px: 2, pb: 2 }}>
                      <Grid container spacing={1}>
                        {SIZE_FIELDS.filter((f) => entry[f.key]).map((f) => (
                          <Grid size={{ xs: 6 }} key={f.key}>
                            <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                            <Typography variant="body2">{entry[f.key]}</Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Collapse>
                </React.Fragment>
              ))}
            </List>
          </>
        )}

        {/* Notes */}
        {editingIndex === null && (
          <TextField
            label="General Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="e.g., Prefers slim fit, runs large in Nike..."
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || editingIndex !== null}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClothingSizesDialog;
